import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Building, Plus, Trash2, Loader2, DoorClosed } from "lucide-react";
import { propertyManagementApi } from "@/services/api";
import { useAsync } from "@/hooks/useAsync";
import { apiError } from "@/lib/format";
import { LoadingState, ErrorState, EmptyState } from "@/components/DataStates";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";

function Field({ label, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-sm">{label}</Label>
      {children}
    </div>
  );
}

function PMDialog({ company, onSaved }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ name: "", contact_name: "", contact_email: "", contact_phone: "", notes: "" });
  useEffect(() => {
    if (open) setForm({
      name: company?.name || "",
      contact_name: company?.contact_name || "",
      contact_email: company?.contact_email || "",
      contact_phone: company?.contact_phone || "",
      notes: company?.notes || "",
    });
  }, [open, company]);

  const save = async () => {
    if (!form.name.trim()) { toast.error("Company name is required"); return; }
    const payload = {
      name: form.name.trim(),
      contact_name: form.contact_name.trim() || null,
      contact_email: form.contact_email.trim() || null,
      contact_phone: form.contact_phone.trim() || null,
      notes: form.notes.trim() || null,
    };
    setBusy(true);
    try {
      if (company) await propertyManagementApi.update(company.id, payload);
      else await propertyManagementApi.create(payload);
      toast.success("Saved");
      setOpen(false);
      onSaved();
    } catch (e) { toast.error(apiError(e, "Could not save")); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {company
          ? <Button size="sm" variant="outline" data-testid={`pm-edit-${company.id}`}>Edit</Button>
          : <Button data-testid="pm-add"><Plus className="mr-1.5 h-4 w-4" /> Add company</Button>}
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{company ? "Edit property management company" : "New property management company"}</DialogTitle>
          <DialogDescription>Assign individual doors within a building to the managing agent that looks after them.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <Field label="Company name *"><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="pm-name" /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Contact name"><Input value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} data-testid="pm-contact-name" /></Field>
            <Field label="Phone"><Input value={form.contact_phone} onChange={(e) => setForm({ ...form, contact_phone: e.target.value })} data-testid="pm-contact-phone" /></Field>
          </div>
          <Field label="Email"><Input value={form.contact_email} onChange={(e) => setForm({ ...form, contact_email: e.target.value })} data-testid="pm-contact-email" /></Field>
          <Field label="Notes"><Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="pm-notes" /></Field>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
          <Button disabled={busy} onClick={save} data-testid="pm-save">{busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null} Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function PropertyManagementTab() {
  const { data, loading, error, reload } = useAsync(() => propertyManagementApi.list(), []);
  const list = Array.isArray(data) ? data : [];

  const remove = async (c) => {
    const msg = c.door_count > 0
      ? `Delete ${c.name}? ${c.door_count} door${c.door_count === 1 ? "" : "s"} will become unassigned.`
      : `Delete ${c.name}?`;
    if (!window.confirm(msg)) return;
    try { await propertyManagementApi.remove(c.id); toast.success("Deleted"); reload(); }
    catch (e) { toast.error(apiError(e, "Could not delete")); }
  };

  if (loading) return <LoadingState label="Loading companies…" />;
  if (error) return <ErrorState message={error} onRetry={reload} />;
  return (
    <div className="max-w-3xl space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-sm text-slate-500">Managing agents you can assign individual doors to.</p>
        <PMDialog onSaved={reload} />
      </div>
      {list.length === 0 ? (
        <EmptyState icon={Building} title="No property management companies" message="Add a company, then assign doors to it from a building's door list." />
      ) : list.map((c) => (
        <Card key={c.id} className="flex flex-wrap items-center justify-between gap-3 p-4" data-testid={`pm-row-${c.id}`}>
          <div className="min-w-0">
            <p className="truncate font-semibold text-slate-900">{c.name}</p>
            <p className="truncate text-sm text-slate-500">
              {[c.contact_name, c.contact_phone, c.contact_email].filter(Boolean).join(" · ") || "No contact details"}
            </p>
            <p className="mt-1 inline-flex items-center gap-1 text-xs text-slate-400">
              <DoorClosed className="h-3.5 w-3.5" /> {c.door_count || 0} door{c.door_count === 1 ? "" : "s"} assigned
            </p>
          </div>
          <div className="flex items-center gap-2">
            <PMDialog company={c} onSaved={reload} />
            <Button size="sm" variant="ghost" className="text-red-600" onClick={() => remove(c)} data-testid={`pm-remove-${c.id}`}><Trash2 className="h-4 w-4" /></Button>
          </div>
        </Card>
      ))}
    </div>
  );
}
